import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FieldGroup } from '@/components/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FormInput, FormField } from '@/shared/ui';

import { cn } from '@/lib/utils';
import { useProjectSiteParams } from '@/shared/lib';
import type { AttachmentsData } from '@/features/consignment-attachment-list';
import type { ConsignmentSummary } from '@/entities/consignments';

import { useConsignmentForm, type ConsignmentFormMode, type ConsignmentFormValues } from '../model';
import { PointsOfContactCard } from './points-of-contact-card';
import { StartingPointCard } from './starting-point-card';
import { ServiceIdentificationCard } from './service-identification-card';
import { AttachmentsCard } from './attachments-card';
import { ConsignmentLinesCard } from './consignment-lines-card';
import { ConsignmentControlCard } from './consignment-control-card';

interface ConsignmentEditFormProps {
  /** Whether the form creates a new consignment or edits an existing one. Defaults to 'edit'. */
  mode?: ConsignmentFormMode;
  /** Required in edit mode; absent in create mode. */
  consignmentId?: string;
  consignmentData?: ConsignmentSummary;
  /** Best-effort initial values, e.g. prefilled from a service request in create mode. */
  prefillValues?: Partial<ConsignmentFormValues>;
  isLoading: boolean;
  isReadOnly?: boolean;
  onSave: (formData: unknown) => void;
}

export interface ConsignmentEditFormRef {
  submit: () => void;
}

export const ConsignmentEditForm = forwardRef<ConsignmentEditFormRef, ConsignmentEditFormProps>(
  ({ mode = 'edit', consignmentId, consignmentData, prefillValues, isReadOnly = false, isLoading, onSave }, ref) => {
    const { t } = useTranslation();
    const { siteId } = useProjectSiteParams();
    const attachmentsRef = useRef<AttachmentsData | null>(null);
    const isEditMode = mode === 'edit';
    const { form, onSubmit } = useConsignmentForm({
      mode,
      consignmentId,
      consignmentData,
      prefillValues,
      onSave,
      getAttachments: () => attachmentsRef.current
    });

    const {
      control,
      register,
      handleSubmit,
      formState: { errors }
    } = form;

    useImperativeHandle(ref, () => ({
      submit: () => {
        handleSubmit(onSubmit)();
      }
    }));

    return (
      <form onSubmit={handleSubmit(onSubmit)} className="relative space-y-6">
        {isLoading && (
          <div className="absolute w-full h-full inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-10">
            <Spinner className="h-8 w-8" />
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Consignment Identification */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('request.id.desc')}</CardTitle>
            </CardHeader>
            <CardContent>
              <FieldGroup>
                {/* Server-assigned identifiers only exist once the consignment is created */}
                {isEditMode && (
                  <FormField label={t('request.cmrNumber.label')} name="consignmentId">
                    <Input id="cmrNumber" value={consignmentData?.cmrNumber || 'N/A'} disabled />
                  </FormField>
                )}

                <FormField label={t('consignment.fields.locationGroup.label', 'Location Group')} name="locationGroup">
                  <Input value={`Site ${siteId}`} disabled />
                </FormField>

                {isEditMode && (
                  <>
                    <FormField label={t('request.createdByLogon.label', 'Created by')} name="createdBy">
                      <Input value={consignmentData?.createdByLogon || 'N/A'} disabled />
                    </FormField>

                    <FormField label={t('request.trackIdnf.label', 'Tracking Code')} name="trackingCode">
                      <Input value={consignmentData?.trackIdnf || 'N/A'} disabled />
                    </FormField>
                  </>
                )}

                <FormInput
                  label={t('request.ownersRef.label', "Owner's Reference")}
                  id="ownersReference"
                  registration={register('ownersReference')}
                  placeholder="Enter owner's reference"
                  disabled={isReadOnly}
                  error={errors.ownersReference?.message}
                />
              </FieldGroup>
            </CardContent>
          </Card>

          <StartingPointCard form={form} consignmentData={consignmentData} isReadOnly={isReadOnly} />
          <ServiceIdentificationCard form={form} consignmentData={consignmentData} isReadOnly={isReadOnly} />
          <ConsignmentControlCard form={form} consignmentId={consignmentId} isReadOnly={isReadOnly} />

          {/* Planned Movement */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('consignment.sections.plannedMovement', 'Planned Movement')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FieldGroup>
                <FormField
                  label={t('request.onSiteDate.label', 'On-Site Date')}
                  name="onSiteDate"
                  error={errors.onSiteDate?.message}>
                  <Controller
                    name="onSiteDate"
                    control={control}
                    render={({ field }) => (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            id="onSiteDate"
                            variant="outline"
                            className={cn(
                              'w-full justify-start text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                            disabled={isReadOnly}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(new Date(field.value), 'PPP') : 'Pick a date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={field.value ? new Date(field.value) : undefined}
                            onSelect={(date) => field.onChange(date?.toISOString())}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  />
                </FormField>

                <FormField
                  label={t('request.transportMode.label', 'Planned Mode')}
                  name="plannedMode"
                  error={errors.plannedMode?.message}>
                  <Controller
                    name="plannedMode"
                    control={control}
                    defaultValue="none"
                    render={({ field }) => (
                      <Select value={field.value || 'none'} onValueChange={field.onChange} disabled={isReadOnly}>
                        <SelectTrigger id="plannedMode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('consignment.plannedMode.none', 'None')}</SelectItem>
                          <SelectItem value="air">{t('consignment.plannedMode.air', 'Air')}</SelectItem>
                          <SelectItem value="road">{t('consignment.plannedMode.road', 'Road')}</SelectItem>
                          <SelectItem value="sea">{t('consignment.plannedMode.sea', 'Sea')}</SelectItem>
                          <SelectItem value="rail">{t('consignment.plannedMode.rail', 'Rail')}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </FormField>

                <FormInput
                  label={t('request.missionNumber.label', 'Mission or Transporter Number')}
                  id="missionTransporterNumber"
                  type="number"
                  registration={register('missionTransporterNumber', { valueAsNumber: true })}
                  placeholder="Enter number"
                  disabled={isReadOnly}
                  error={errors.missionTransporterNumber?.message}
                />
              </FieldGroup>
            </CardContent>
          </Card>

          {/* SLA/Invoice/Packing List Links */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('consignment.sections.documents.label', 'SLA/Invoice/Packing List Links')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FormInput
                label={t('newcmr.cmr.links', 'SLA/Invoice/Packing List Links')}
                id="cmrLinks"
                type="text"
                registration={register('cmrLinks')}
                placeholder={t('consignment.fields.cmrLinks.placeholder', 'Enter SLA, Invoice, or Packing List links')}
                disabled={isReadOnly}
                error={errors.cmrLinks?.message}
              />
            </CardContent>
          </Card>

          {/* Points of Contact */}
          <PointsOfContactCard
            control={control}
            watch={form.watch}
            setValue={form.setValue}
            consignmentData={consignmentData}
            isReadOnly={isReadOnly}
          />

          {/* Attachments — listing is fetched by consignment id, so create mode has nothing to show */}
          {isEditMode && consignmentId && (
            <AttachmentsCard
              consignmentId={consignmentId}
              isReadOnly={isReadOnly}
              onAttachmentsChange={(attachments) => {
                attachmentsRef.current = attachments;
              }}
            />
          )}

          {/* Consignment Lines — only available once the consignment exists */}
          {isEditMode && consignmentId && (
            <ConsignmentLinesCard
              consignmentId={consignmentId}
              consignmentStatus={consignmentData?.statusCode}
              isReadOnly={isReadOnly}
            />
          )}
        </div>
      </form>
    );
  }
);
